import {
  getCompatibilityFallbackHandlerDeployment,
  getCreateCallDeployment,
  getMultiSendCallOnlyDeployment,
  getMultiSendDeployment,
  getProxyFactoryDeployment,
  getSafeL2SingletonDeployment,
  getSafeSingletonDeployment,
  getSignMessageLibDeployment,
  getSimulateTxAccessorDeployment,
} from '@safe-global/safe-deployments'
import type { DeploymentFilter, SingletonDeployment } from '@safe-global/safe-deployments/dist/types'
import { safeDeploymentsVersions } from '@safe-global/protocol-kit/dist/src/contracts/config'
import type { ContractNetworkConfig, ContractNetworksConfig } from '@safe-global/protocol-kit/dist/src/types'
import semverSatisfies from 'semver/functions/satisfies'

// SAFE4 change: all Safe contract addresses can be supplied as overrides (via magic-network URL
// params → chain config). Any slot left undefined falls back to the canonical (deterministic)
// address, which is enough for the read-only slots that are never actually invoked on this chain.
export type SafeContractOverrides = {
  safeSingletonAddress?: string
  safeProxyFactoryAddress?: string
  fallbackHandlerAddress?: string
  multisendAddress?: string
  multisendCallOnlyAddress?: string
}

// Back-compat alias for callers that only override multisend.
export type MultiSendContractOverrides = Pick<SafeContractOverrides, 'multisendAddress' | 'multisendCallOnlyAddress'>

// Extract the Safe contract address overrides carried on a (custom) chain config.
export const getChainContractOverrides = (chain?: Partial<SafeContractOverrides> | null): SafeContractOverrides => ({
  safeSingletonAddress: chain?.safeSingletonAddress,
  safeProxyFactoryAddress: chain?.safeProxyFactoryAddress,
  fallbackHandlerAddress: chain?.fallbackHandlerAddress,
  multisendAddress: chain?.multisendAddress,
  multisendCallOnlyAddress: chain?.multisendCallOnlyAddress,
})

type SafeDeploymentVersionConfig = (typeof safeDeploymentsVersions)[string]
const SAFE_DEPLOYMENT_VERSION_ORDER = ['1.4.1', '1.3.0', '1.2.0', '1.1.1', '1.0.0']

const getSafeDeploymentVersionConfig = (safeVersion: string): SafeDeploymentVersionConfig => {
  const matchedVersion = SAFE_DEPLOYMENT_VERSION_ORDER.find((version) => semverSatisfies(safeVersion, version))
  return safeDeploymentsVersions[matchedVersion || '1.3.0']
}

const getDeploymentAddress = (
  getDeployment: (filter?: DeploymentFilter) => SingletonDeployment | undefined,
  chainId: string,
  version?: string,
): string | undefined => {
  if (!version) {
    return undefined
  }

  // SAFE4 change: fall back to the canonical (deterministic) default address when the chain is
  // absent from @safe-global/safe-deployments. These canonical addresses are only used for slots
  // that are never actually invoked on this chain (signMessageLib / createCall / simulateTxAccessor);
  // the real Safe/Factory/FallbackHandler/MultiSend addresses come from the overrides below.
  return (
    getDeployment({ version, network: chainId, released: true })?.defaultAddress ??
    getDeployment({ version, released: true })?.defaultAddress ??
    getDeployment({ version })?.defaultAddress
  )
}

const normalizeAddressOverride = (address?: string): string | undefined => {
  const trimmed = address?.trim()
  return trimmed ? trimmed : undefined
}

const getBaseContractNetworkConfig = (
  chainId: string,
  safeVersion: string,
  isL1SafeMasterCopy: boolean,
  overrides: SafeContractOverrides,
): ContractNetworkConfig | undefined => {
  const deploymentVersions = getSafeDeploymentVersionConfig(safeVersion)

  const safeMasterCopyAddress =
    normalizeAddressOverride(overrides.safeSingletonAddress) ??
    (isL1SafeMasterCopy
      ? getDeploymentAddress(getSafeSingletonDeployment, chainId, deploymentVersions.safeMasterCopyVersion)
      : getDeploymentAddress(getSafeL2SingletonDeployment, chainId, deploymentVersions.safeMasterCopyL2Version) ||
        getDeploymentAddress(getSafeSingletonDeployment, chainId, deploymentVersions.safeMasterCopyVersion))

  const safeProxyFactoryAddress =
    normalizeAddressOverride(overrides.safeProxyFactoryAddress) ??
    getDeploymentAddress(getProxyFactoryDeployment, chainId, deploymentVersions.safeProxyFactoryVersion)

  const fallbackHandlerAddress =
    normalizeAddressOverride(overrides.fallbackHandlerAddress) ??
    getDeploymentAddress(
      getCompatibilityFallbackHandlerDeployment,
      chainId,
      deploymentVersions.compatibilityFallbackHandler,
    )

  const multiSendAddress =
    normalizeAddressOverride(overrides.multisendAddress) ??
    getDeploymentAddress(getMultiSendDeployment, chainId, deploymentVersions.multiSendVersion)
  const multiSendCallOnlyAddress =
    normalizeAddressOverride(overrides.multisendCallOnlyAddress) ??
    getDeploymentAddress(getMultiSendCallOnlyDeployment, chainId, deploymentVersions.multiSendCallOnlyVersion)

  const signMessageLibAddress = getDeploymentAddress(
    getSignMessageLibDeployment,
    chainId,
    deploymentVersions.signMessageLibVersion,
  )
  const createCallAddress = getDeploymentAddress(getCreateCallDeployment, chainId, deploymentVersions.createCallVersion)
  const simulateTxAccessorAddress = getDeploymentAddress(
    getSimulateTxAccessorDeployment,
    chainId,
    deploymentVersions.createCallVersion,
  )

  if (
    !safeMasterCopyAddress ||
    !safeProxyFactoryAddress ||
    !multiSendAddress ||
    !multiSendCallOnlyAddress ||
    !fallbackHandlerAddress ||
    !signMessageLibAddress ||
    !createCallAddress ||
    !simulateTxAccessorAddress
  ) {
    return undefined
  }

  return {
    safeMasterCopyAddress,
    safeProxyFactoryAddress,
    multiSendAddress,
    multiSendCallOnlyAddress,
    fallbackHandlerAddress,
    signMessageLibAddress,
    createCallAddress,
    simulateTxAccessorAddress,
  }
}

export const getContractNetworksForOverrides = (
  chainId: string,
  safeVersion: string,
  isL1SafeMasterCopy: boolean,
  overrides: SafeContractOverrides,
): ContractNetworksConfig | undefined => {
  const multisendAddress = normalizeAddressOverride(overrides.multisendAddress)
  const multisendCallOnlyAddress = normalizeAddressOverride(overrides.multisendCallOnlyAddress)

  // Overrides are only applied for custom chains, which always carry the multisend pair.
  if (!multisendAddress || !multisendCallOnlyAddress) {
    return undefined
  }

  const contractConfig = getBaseContractNetworkConfig(chainId, safeVersion, isL1SafeMasterCopy, overrides)

  if (!contractConfig) {
    return undefined
  }

  return {
    [chainId]: contractConfig,
  }
}
